/* global G */
import { curry, setKey } from '@gaia/util'

const _getActionState = module => module[G.STATE][G.ACTION][G.COMPONENT][G.STATE]

/**
 * Sets `animateTransition` in the action components state to `animate`.
 *
 * @param {boolean} animate                     whether to animate or not
 * @param {Gaia.AppModule.Spec} module          module composition
 * @param {Gaia.Component.Spec} component       action component
 * @param {Gaia.AppModule.EventHandler} event   triggered event
 * @returns {void}
 */
const setTransition = (animate, module, component, event) => {
  const actionState = _getActionState(module)

  setKey(animate ?? true, 'animateTransition', actionState)
}

/**
 * Sets `animateTransition` in the action components state for the content
 * to `animate`.
 *
 * @param {boolean} animate                     whether to animate or not
 * @param {Gaia.AppModule.Spec} module          module composition
 * @param {Gaia.Component.Spec} component       action component
 * @param {Gaia.AppModule.EventHandler} event   triggered event
 * @returns {void}
 */
export const setContentTransition = (animate, module, component, event) => {
  const actionState = _getActionState(module)
  const currentState = actionState.animateTransition

  !currentState && setKey({}, 'animateTransition', actionState)
  setKey(animate ?? true, 'content', actionState.animateTransition)
}

/**
 * Sets `animateTransition` in the action components state for the app bar
 * to `animate`.
 *
 * @param {boolean} animate                     whether to animate or not
 * @param {Gaia.AppModule.Spec} module          module composition
 * @param {Gaia.Component.Spec} component       action component
 * @param {Gaia.AppModule.EventHandler} event   triggered event
 * @returns {void}
 */
export const setAppBarTransition = (animate, module, component, event) => {
  const actionState = _getActionState(module)
  const currentState = actionState.animateTransition

  !currentState && setKey({}, 'animateTransition', actionState)
  setKey(animate ?? true, 'appBar', actionState.animateTransition)
}

/**
 * Sets `transitionDirection` in the action components state to `direction`.
 *
 * @param {RouterState['direction']} direction  the direction
 * @param {Gaia.AppModule.Spec} module          module composition
 * @param {Gaia.Component.Spec} component       action component
 * @param {Gaia.AppModule.EventHandler} event   triggered event
 * @returns {void}
 */
export const setTransitionDirection = (direction, module, component, event) => {
  const actionState = _getActionState(module)

  setKey(direction, 'transitionDirection', actionState)
}

/**
 * Sets `transitionDirection` in the action components state for the app bar
 * to `direction`.
 *
 * @param {RouterState['direction']} direction  the direction
 * @param {Gaia.AppModule.Spec} module          module composition
 * @param {Gaia.Component.Spec} component       action component
 * @param {Gaia.AppModule.EventHandler} event   triggered event
 * @returns {void}
 */
export const setAppBarTransitionDirection = (direction, module, component, event) => {
  const actionState = _getActionState(module)
  const currentState = actionState.transitionDirection

  !currentState && setKey({}, 'transitionDirection', actionState)
  direction && setKey(direction, 'appBar', actionState.transitionDirection)
}

/**
 * Sets `transitionDirection` in the action components state for the content
 * to `direction`.
 *
 * @param {RouterState['direction']} direction  the direction
 * @param {Gaia.AppModule.Spec} module          module composition
 * @param {Gaia.Component.Spec} component       action component
 * @param {Gaia.AppModule.EventHandler} event   triggered event
 * @returns {void}
 */
export const setContentTransitionDirection = (direction, module, component, event) => {
  const actionState = _getActionState(module)
  const currentState = actionState.transitionDirection

  !currentState && setKey({}, 'transitionDirection', actionState)
  direction && setKey(direction, 'content', actionState.transitionDirection)
}

/**
 * Sets `transitionDirection` in the action components state for the content
 * to `direction`.
 *
 * @param {Gaia.AppModule.EventHandler} handler   triggered event
 */
export const withDisabledTransition = handler => curry((module, component, event) => {
  setTransition(false, module, component, event)
  return handler(module, component, event)
})

export default curry(setTransition)
